/**
 * Geocodificacion de direcciones para el selector de ubicacion, con un
 * proveedor intercambiable y respeto estricto al presupuesto y a las politicas
 * del servicio. Por defecto se usa el Nominatim publico de OpenStreetMap.
 *
 * Reglas que se cumplen aqui (https://operations.osmfoundation.org/policies/nominatim/):
 *   * Solo se busca cuando la persona lo pide de forma explicita: NUNCA hay
 *     autocompletado ni busquedas al escribir.
 *   * Espaciado de red: dentro de esta instancia las busquedas se serializan y
 *     se separan con `minIntervalMs` (1000 ms por defecto), de modo que una
 *     pestana no encadene solicitudes mas rapido que 1/s. LIMITACION: al ser un
 *     componente de navegador, cada pestana/carga tiene su propia cola, por lo
 *     que NO se garantiza un limite global de 1 req/s para toda la aplicacion
 *     (varios usuarios o pestanas pueden sumar). No se implementan mecanismos
 *     para eludir los limites del proveedor; si responde 429/403, el usuario
 *     puede seguir con la seleccion manual en el mapa.
 *   * Se aplica cache en memoria por consulta normalizada para no repetir
 *     solicitudes identicas.
 *   * Se identifica la app con un `Referer`/`User-Agent` del navegador y, si se
 *     configura, un `email` de contacto.
 *   * Se distingue entre limite de uso, servicio no disponible, error de red y
 *     cancelacion para poder mostrar mensajes utiles.
 *
 * El proveedor es sustituible: cualquier implementacion de `GeocodingProvider`
 * puede inyectarse en el selector sin tocar el componente.
 */

import { isValidLatitude, isValidLongitude, type MapCoordinates } from "./maps";

/** Punto geocodificado, listo para centrar el mapa y confirmar la ubicacion. */
export interface GeocodeResult extends MapCoordinates {
  /** Identificador estable (osm_type + osm_id, o place_id). */
  id: string;
  /** Texto completo de la direccion devuelto por el proveedor. */
  label: string;
  /** Nombre principal del lugar (primera parte de la direccion). */
  name: string;
  /** Resto de la direccion, sin el nombre principal. */
  address: string;
  /** Ciudad, distrito o departamento, cuando el proveedor lo distingue. */
  locality: string;
  /** Tipo de lugar (city, house, road, etc.). */
  type: string | null;
  /** Importancia relativa reportada por el proveedor. */
  importance: number;
}

export type GeocodingErrorCode =
  "invalid_query" | "rate_limited" | "unavailable" | "network" | "aborted" | "unknown";

/** Error tipado del geocodificador para traducirlo a mensajes de la interfaz. */
export class GeocodingError extends Error {
  readonly code: GeocodingErrorCode;

  constructor(code: GeocodingErrorCode, message: string) {
    super(message);
    this.name = "GeocodingError";
    this.code = code;
  }
}

export interface GeocodeSearchParams {
  signal?: AbortSignal;
  /** Maximo de resultados (1..10). Por defecto 5. */
  limit?: number;
  /** Codigos de pais separados por coma (ISO 3166-1 alpha2). Por defecto "pe". */
  countryCodes?: string;
  /** Idioma de las direcciones. Por defecto "es". */
  acceptLanguage?: string;
}

/** Contrato minimo de un proveedor de geocodificacion. */
export interface GeocodingProvider {
  readonly id: string;
  readonly attribution: string;
  readonly attributionUrl: string;
  readonly usagePolicyUrl: string;
  search(query: string, params?: GeocodeSearchParams): Promise<GeocodeResult[]>;
}

interface NominatimRaw {
  place_id?: number;
  osm_type?: string;
  osm_id?: number;
  lat?: string;
  lon?: string;
  name?: string;
  display_name?: string;
  type?: string;
  category?: string;
  class?: string;
  importance?: number;
  address?: Record<string, string>;
}

export interface NominatimProviderOptions {
  baseUrl?: string;
  /** Separacion minima entre solicitudes reales (ms). Por defecto 1000. */
  minIntervalMs?: number;
  /** Vigencia de la cache en memoria (ms). Por defecto 10 minutos. */
  cacheTtlMs?: number;
  /** `fetch` inyectable para pruebas. */
  fetchImpl?: typeof fetch;
  /** Email de contacto recomendado por la politica de uso. */
  contactEmail?: string;
}

const LOCALITY_KEYS = [
  "city",
  "town",
  "village",
  "municipality",
  "city_district",
  "district",
  "county",
  "state_district",
  "state",
  "region",
] as const;

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(Math.max(value, min), max);
}

function isAbortError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    (error as { name?: unknown }).name === "AbortError"
  );
}

function pickLocality(address: Record<string, string> | undefined): string {
  if (!address) return "";
  for (const key of LOCALITY_KEYS) {
    const value = address[key];
    if (typeof value === "string" && value.trim() !== "") return value.trim();
  }
  return "";
}

function normalizeResult(raw: NominatimRaw, index: number): GeocodeResult | null {
  const latitude = Number(raw.lat);
  const longitude = Number(raw.lon);
  if (!isValidLatitude(latitude) || !isValidLongitude(longitude)) return null;

  const display = (raw.display_name ?? "").trim();
  const primary = (raw.name ?? "").trim() || display.split(",")[0]?.trim() || "";

  let address = display;
  if (primary && address.toLowerCase().startsWith(primary.toLowerCase())) {
    address = address.slice(primary.length).replace(/^[,\s]+/, "");
  }

  const id =
    raw.osm_type && raw.osm_id != null
      ? `${raw.osm_type}${raw.osm_id}`
      : raw.place_id != null
        ? `place-${raw.place_id}`
        : `result-${index}`;

  return {
    id,
    label: display || primary,
    name: primary,
    address: address.trim(),
    locality: pickLocality(raw.address),
    latitude,
    longitude,
    type: raw.type ?? raw.category ?? raw.class ?? null,
    importance: Number(raw.importance ?? 0),
  };
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new GeocodingError("aborted", "Busqueda cancelada."));
      return;
    }
    const cleanup = () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
    };
    const timer = setTimeout(() => {
      cleanup();
      resolve();
    }, ms);
    const onAbort = () => {
      cleanup();
      reject(new GeocodingError("aborted", "Busqueda cancelada."));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Proveedor Nominatim (OpenStreetMap). Serializa las solicitudes dentro de la
 * instancia y las espacia `minIntervalMs` (1 req/s por pestana por defecto), y
 * cachea cada consulta. No garantiza un limite global por app (ver aviso arriba).
 */
export class NominatimGeocodingProvider implements GeocodingProvider {
  readonly id = "nominatim";
  readonly attribution = "Búsqueda de direcciones por OpenStreetMap";
  readonly attributionUrl = "https://www.openstreetmap.org/copyright";
  readonly usagePolicyUrl = "https://operations.osmfoundation.org/policies/nominatim/";

  private readonly baseUrl: string;
  private readonly minIntervalMs: number;
  private readonly cacheTtlMs: number;
  private readonly fetchImpl: typeof fetch;
  private readonly contactEmail: string | undefined;
  private readonly cache = new Map<string, { expires: number; results: GeocodeResult[] }>();
  private queue: Promise<unknown> = Promise.resolve();
  private lastStart = 0;

  constructor(options: NominatimProviderOptions = {}) {
    this.baseUrl = (options.baseUrl ?? "https://nominatim.openstreetmap.org").replace(/\/+$/, "");
    this.minIntervalMs = Math.max(0, options.minIntervalMs ?? 1000);
    this.cacheTtlMs = Math.max(0, options.cacheTtlMs ?? 10 * 60 * 1000);
    this.contactEmail = options.contactEmail;
    this.fetchImpl =
      options.fetchImpl ??
      ((input, init) => {
        if (typeof globalThis.fetch !== "function") {
          return Promise.reject(
            new GeocodingError("network", "El buscador de direcciones no esta disponible."),
          );
        }
        return globalThis.fetch(input as RequestInfo, init);
      });
  }

  search(query: string, params: GeocodeSearchParams = {}): Promise<GeocodeResult[]> {
    const trimmed = typeof query === "string" ? query.trim() : "";
    if (trimmed.length < 3) {
      return Promise.reject(
        new GeocodingError("invalid_query", "Escribe al menos 3 caracteres para buscar la direccion."),
      );
    }

    const limit = clamp(Math.trunc(params.limit ?? 5), 1, 10);
    const countryCodes = params.countryCodes ?? "pe";
    const acceptLanguage = params.acceptLanguage ?? "es";
    const key = `${countryCodes}|${acceptLanguage}|${limit}|${trimmed.toLowerCase()}`;

    const cached = this.cache.get(key);
    if (cached && cached.expires > Date.now()) return Promise.resolve(cached.results);

    const run = this.queue.then(() =>
      this.runSearch(trimmed, limit, countryCodes, acceptLanguage, params.signal, key),
    );
    this.queue = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  private async runSearch(
    query: string,
    limit: number,
    countryCodes: string,
    acceptLanguage: string,
    signal: AbortSignal | undefined,
    cacheKey: string,
  ): Promise<GeocodeResult[]> {
    if (signal?.aborted) throw new GeocodingError("aborted", "Busqueda cancelada.");

    const wait = this.lastStart + this.minIntervalMs - Date.now();
    if (wait > 0) await delay(wait, signal);
    this.lastStart = Date.now();

    const url = new URL(`${this.baseUrl}/search`);
    url.searchParams.set("format", "jsonv2");
    url.searchParams.set("q", query);
    url.searchParams.set("limit", String(limit));
    url.searchParams.set("addressdetails", "1");
    if (countryCodes) url.searchParams.set("countrycodes", countryCodes);
    url.searchParams.set("accept-language", acceptLanguage);
    if (this.contactEmail) url.searchParams.set("email", this.contactEmail);

    const init: RequestInit = { method: "GET", headers: { Accept: "application/json" } };
    if (signal) init.signal = signal;

    let response: Response;
    try {
      response = await this.fetchImpl(url.toString(), init);
    } catch (error) {
      if (isAbortError(error)) throw new GeocodingError("aborted", "Busqueda cancelada.");
      throw new GeocodingError("network", "No pudimos conectar con el buscador de direcciones.");
    }

    if (response.status === 429 || response.status === 403) {
      throw new GeocodingError(
        "rate_limited",
        "El buscador esta limitando las solicitudes. Espera unos segundos e intentalo de nuevo.",
      );
    }
    if (!response.ok) {
      throw new GeocodingError(
        "unavailable",
        "El buscador de direcciones no esta disponible en este momento.",
      );
    }

    let data: unknown;
    try {
      data = await response.json();
    } catch {
      throw new GeocodingError("unavailable", "El buscador devolvio una respuesta no valida.");
    }
    if (!Array.isArray(data)) {
      throw new GeocodingError("unavailable", "El buscador devolvio una respuesta no valida.");
    }

    const results = data
      .map((item, index) => normalizeResult(item as NominatimRaw, index))
      .filter((result): result is GeocodeResult => result !== null)
      .slice(0, limit);

    this.cache.set(cacheKey, { expires: Date.now() + this.cacheTtlMs, results });
    return results;
  }
}

/** Proveedor por defecto de la app (Nominatim publico). */
export const nominatimGeocodingProvider = new NominatimGeocodingProvider();

/** Enlace obligatorio de atribucion del proveedor. */
export function geocodingAttribution(provider: GeocodingProvider): {
  text: string;
  url: string;
  policyUrl: string;
} {
  return { text: provider.attribution, url: provider.attributionUrl, policyUrl: provider.usagePolicyUrl };
}
